import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import {
  checkVendorDirectoryDuplicates,
  createVendorDirectoryVendor,
  getVendorDirectoryTaxonomy,
} from "../../api/clientVendorDirectoryApi";
import { ClientPortalError } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.create";

const VENDOR_KINDS = ["registered_company", "individual_professional", "service_area_provider"];
const CONTACT_TYPES = ["phone", "whatsapp", "email"];

function FormSection({ title, description, children }) {
  return (
    <section className="cvd-vendor-create__section">
      <div className="cvd-vendor-create__section-head">
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      <div className="cvd-vendor-create__section-body">{children}</div>
    </section>
  );
}

function Field({ id, label, hint, badge, children, wide = false }) {
  return (
    <div className={`cvd-field${wide ? " cvd-field--wide" : ""}`}>
      <div className="cvd-field__label-row">
        <label htmlFor={id}>{label}</label>
        {badge ? <span className={`cvd-field__badge cvd-field__badge--${badge.tone}`}>{badge.label}</span> : null}
      </div>
      {children}
      {hint ? <p className="cvd-field__hint">{hint}</p> : null}
    </div>
  );
}

export default function ClientVendorDirectoryCreatePage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t } = useLocale();
  const navigate = useNavigate();
  const [taxonomy, setTaxonomy] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [form, setForm] = useState({
    displayName: "",
    legalName: "",
    vendorKind: "registered_company",
    categoryKey: "plumbing",
    municipalityId: "",
    contactType: "whatsapp",
    contactValue: "",
    contactIsPublic: true,
    lifecycleStatus: "discovered",
    creationSource: "manual_admin",
    initialNote: "",
    duplicateWarningAcknowledged: false,
  });

  useEffect(() => {
    getVendorDirectoryTaxonomy(clientViewId)
      .then((body) => setTaxonomy(body.taxonomy || body))
      .catch(() => setTaxonomy(null));
  }, [clientViewId]);

  const municipalities = taxonomy?.municipalities || [];
  const categories = (taxonomy?.categories || []).filter((category) => category.isMvp || category.isActive !== false);
  const viewName = activeView?.name || "CasaMapa";
  const basePath = `${serviciosBase}/proveedores`;
  const requiredBadge = { tone: "required", label: t(`${COPY_PREFIX}.required`) };
  const optionalBadge = { tone: "optional", label: t(`${COPY_PREFIX}.optional`) };
  const recommendedBadge = { tone: "recommended", label: t(`${COPY_PREFIX}.recommended`) };

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const runDuplicateCheck = async () => {
    if (!form.displayName.trim() && !form.contactValue.trim()) {
      setWarnings([]);
      return;
    }
    try {
      const body = await checkVendorDirectoryDuplicates(clientViewId, {
        displayName: form.displayName,
        contactType: form.contactType,
        contactValue: form.contactValue,
        municipalityId: form.municipalityId || undefined,
      });
      setWarnings(body.warnings || []);
    } catch {
      setWarnings([]);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setErrorMessage("");
    try {
      if (!warnings.length) await runDuplicateCheck();
      const body = await createVendorDirectoryVendor(clientViewId, {
        ...form,
        municipalityId: form.municipalityId || undefined,
        duplicateWarningAcknowledged: warnings.length > 0 ? form.duplicateWarningAcknowledged : true,
      });
      navigate(`${basePath}/${body.vendor?.vendor?.id || body.vendor?.id}`);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.submitError`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-content cvd-admin cvd-vendor-create">
      <header className="cvd-vendor-create__header">
        <Link className="cvd-btn cvd-btn--ghost cvd-vendor-create__back" to={basePath}>
          ← {t(`${COPY_PREFIX}.backToList`)}
        </Link>
        <div className="cvd-vendor-create__header-main">
          <div className="cvd-vendor-create__header-copy">
            <p className="cvd-vendor-create__eyebrow">{t(`${COPY_PREFIX}.eyebrow`, { viewName })}</p>
            <h1 className="cvd-vendor-create__title">{t(`${COPY_PREFIX}.title`)}</h1>
            <p className="cvd-vendor-create__intro">{t(`${COPY_PREFIX}.intro`, { viewName })}</p>
          </div>
          <aside className="cvd-vendor-create__aside">
            <h2>{t(`${COPY_PREFIX}.nextStepsTitle`)}</h2>
            <ol className="cvd-vendor-create__steps">
              <li>{t(`${COPY_PREFIX}.nextSteps.draft`)}</li>
              <li>{t(`${COPY_PREFIX}.nextSteps.profile`)}</li>
              <li>{t(`${COPY_PREFIX}.nextSteps.publish`)}</li>
            </ol>
          </aside>
        </div>
      </header>

      {errorMessage ? <ClientPortalError message={errorMessage} /> : null}

      <form className="cvd-vendor-create__form" onSubmit={handleSubmit}>
        <div className="cvd-vendor-create__layout">
          <div className="cvd-vendor-create__main">
            <FormSection
              title={t(`${COPY_PREFIX}.sections.identity.title`)}
              description={t(`${COPY_PREFIX}.sections.identity.description`)}
            >
              <div className="cvd-field-grid">
                <Field
                  id="displayName"
                  label={t(`${COPY_PREFIX}.fields.displayName`)}
                  hint={t(`${COPY_PREFIX}.hints.displayName`)}
                  badge={requiredBadge}
                >
                  <input
                    id="displayName"
                    required
                    value={form.displayName}
                    onChange={(event) => update("displayName", event.target.value)}
                    onBlur={runDuplicateCheck}
                    placeholder={t(`${COPY_PREFIX}.placeholders.displayName`)}
                  />
                </Field>
                <Field
                  id="legalName"
                  label={t(`${COPY_PREFIX}.fields.legalName`)}
                  hint={t(`${COPY_PREFIX}.hints.legalName`)}
                  badge={optionalBadge}
                >
                  <input
                    id="legalName"
                    value={form.legalName}
                    onChange={(event) => update("legalName", event.target.value)}
                    placeholder={t(`${COPY_PREFIX}.placeholders.legalName`)}
                  />
                </Field>
                <Field
                  id="vendorKind"
                  label={t(`${COPY_PREFIX}.fields.vendorKind`)}
                  hint={t(`${COPY_PREFIX}.hints.vendorKind`)}
                >
                  <select
                    id="vendorKind"
                    value={form.vendorKind}
                    onChange={(event) => update("vendorKind", event.target.value)}
                  >
                    {VENDOR_KINDS.map((value) => (
                      <option key={value} value={value}>
                        {t(`${COPY_PREFIX}.vendorKinds.${value}`)}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </FormSection>

            <FormSection
              title={t(`${COPY_PREFIX}.sections.classification.title`)}
              description={t(`${COPY_PREFIX}.sections.classification.description`)}
            >
              <div className="cvd-field-grid">
                <Field
                  id="categoryKey"
                  label={t(`${COPY_PREFIX}.fields.categoryKey`)}
                  hint={t(`${COPY_PREFIX}.hints.categoryKey`)}
                  badge={requiredBadge}
                >
                  <select
                    id="categoryKey"
                    value={form.categoryKey}
                    onChange={(event) => update("categoryKey", event.target.value)}
                  >
                    {categories.map((category) => (
                      <option key={category.key} value={category.key}>
                        {category.name || category.nameEs}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  id="municipalityId"
                  label={t(`${COPY_PREFIX}.fields.municipalityId`)}
                  hint={t(`${COPY_PREFIX}.hints.municipalityId`)}
                  badge={recommendedBadge}
                >
                  <select
                    id="municipalityId"
                    value={form.municipalityId}
                    onChange={(event) => update("municipalityId", event.target.value)}
                  >
                    <option value="">{t(`${COPY_PREFIX}.placeholders.municipality`)}</option>
                    {municipalities.map((municipality) => (
                      <option key={municipality.id} value={municipality.id}>
                        {municipality.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </FormSection>

            <FormSection
              title={t(`${COPY_PREFIX}.sections.contact.title`)}
              description={t(`${COPY_PREFIX}.sections.contact.description`)}
            >
              <div className="cvd-field-grid">
                <Field id="contactType" label={t(`${COPY_PREFIX}.fields.contactType`)} badge={optionalBadge}>
                  <select
                    id="contactType"
                    value={form.contactType}
                    onChange={(event) => update("contactType", event.target.value)}
                  >
                    {CONTACT_TYPES.map((value) => (
                      <option key={value} value={value}>
                        {t(`${COPY_PREFIX}.contactTypes.${value}`)}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  id="contactValue"
                  label={t(`${COPY_PREFIX}.fields.contactValue`)}
                  hint={t(`${COPY_PREFIX}.hints.contactValue.${form.contactType}`)}
                  badge={optionalBadge}
                >
                  <input
                    id="contactValue"
                    value={form.contactValue}
                    onChange={(event) => update("contactValue", event.target.value)}
                    onBlur={runDuplicateCheck}
                    placeholder={t(`${COPY_PREFIX}.placeholders.contactValue.${form.contactType}`)}
                    inputMode={form.contactType === "email" ? "email" : "tel"}
                  />
                </Field>
              </div>
              <label className="cvd-vendor-create__switch">
                <input
                  type="checkbox"
                  checked={form.contactIsPublic}
                  onChange={(event) => update("contactIsPublic", event.target.checked)}
                />
                <span>
                  <strong>{t(`${COPY_PREFIX}.fields.contactIsPublic`)}</strong>
                  <em>{t(`${COPY_PREFIX}.hints.contactIsPublic`)}</em>
                </span>
              </label>
            </FormSection>

            {warnings.length > 0 ? (
              <section className="cvd-vendor-create__warnings" role="alert">
                <h2>{t(`${COPY_PREFIX}.duplicates.title`)}</h2>
                <p>{t(`${COPY_PREFIX}.duplicates.description`)}</p>
                <ul className="cvd-vendor-create__warning-list">
                  {warnings.map((warning) => (
                    <li key={`${warning.type}-${warning.vendorId}`}>
                      <strong>{warning.displayName}</strong>
                      <span>
                        {warning.type} · {warning.publicId}
                      </span>
                    </li>
                  ))}
                </ul>
                <label className="cvd-vendor-create__switch">
                  <input
                    type="checkbox"
                    required={warnings.length > 0}
                    checked={form.duplicateWarningAcknowledged}
                    onChange={(event) => update("duplicateWarningAcknowledged", event.target.checked)}
                  />
                  <span>
                    <strong>{t(`${COPY_PREFIX}.duplicates.acknowledge`)}</strong>
                  </span>
                </label>
              </section>
            ) : null}

            <section className="cvd-vendor-create__finish">
              <div className="cvd-vendor-create__section-head">
                <h2>{t(`${COPY_PREFIX}.sections.notes.title`)}</h2>
                <p>{t(`${COPY_PREFIX}.sections.notes.description`)}</p>
              </div>
              <Field
                id="initialNote"
                label={t(`${COPY_PREFIX}.fields.initialNote`)}
                hint={t(`${COPY_PREFIX}.hints.initialNote`)}
                badge={optionalBadge}
                wide
              >
                <textarea
                  id="initialNote"
                  rows={2}
                  value={form.initialNote}
                  onChange={(event) => update("initialNote", event.target.value)}
                  placeholder={t(`${COPY_PREFIX}.placeholders.initialNote`)}
                />
              </Field>
              <div className="cvd-vendor-create__finish-bar">
                <div className="cvd-vendor-create__finish-copy">
                  <p className="cvd-vendor-create__finish-title">{t(`${COPY_PREFIX}.footerTitle`)}</p>
                  <p>{t(`${COPY_PREFIX}.footerHint`)}</p>
                </div>
                <div className="cvd-vendor-create__finish-actions">
                  <Link className="cvd-btn cvd-btn--ghost" to={basePath}>
                    {t(`${COPY_PREFIX}.cancel`)}
                  </Link>
                  <button type="submit" className="cvd-btn cvd-btn--primary" disabled={submitting}>
                    {submitting ? t(`${COPY_PREFIX}.submitting`) : t(`${COPY_PREFIX}.submit`)}
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </form>
    </div>
  );
}
